import { useState } from "react";
import { useI18n } from "@/i18n";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { BookText, FileType2, Download, Loader2, FileDown } from "lucide-react";
import { toast } from "sonner";

export default function ExportModal({ open, onOpenChange, project }) {
  const { t } = useI18n();
  const [busy, setBusy] = useState(null);

  const download = async (format) => {
    setBusy(format);
    try {
      const res = await api.get(`/projects/${project.id}/export`, { params: { format }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      const safe = (project.title || "libro").replace(/[^a-z0-9 \-_]/gi, "").trim() || "libro";
      a.download = `${safe}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(t("toast.exported"));
    } catch {
      toast.error(t("toast.error"));
    } finally {
      setBusy(null);
    }
  };

  const card = (format, icon, title, desc, testid) => {
    const Icon = icon;
    const loading = busy === format;
    return (
      <div className="card-surface rounded-xl p-6 flex flex-col">
        <div className="w-11 h-11 rounded-xl bg-gold/10 flex items-center justify-center mb-4">
          <Icon className="w-5 h-5 text-gold" />
        </div>
        <h3 className="font-serif text-lg font-semibold text-slate-100">{title}</h3>
        <p className="mt-1.5 text-xs text-slate-400 leading-relaxed flex-1">{desc}</p>
        <Button onClick={() => download(format)} disabled={!!busy} className="mt-5 bg-gold hover:bg-gold-light text-ink-900 font-semibold" data-testid={testid}>
          {loading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> {t("export.preparing")}</> : <><Download className="w-4 h-4 mr-2" /> {t("export.download")}</>}
        </Button>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-ink-800 border-white/10 text-slate-100 max-w-2xl">
        <DialogHeader><DialogTitle className="font-serif text-2xl flex items-center gap-2"><FileDown className="w-5 h-5 text-gold" /> {t("export.title")}</DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-4 mt-2">
          {card("epub", BookText, t("export.epub"), t("export.epubDesc"), "export-epub-button")}
          {card("pdf", FileType2, t("export.pdf"), t("export.pdfDesc"), "export-pdf-button")}
        </div>
      </DialogContent>
    </Dialog>
  );
}
